// PEA withdrawal simulator (estimate, rules in force since 2026-01-01; the
// rates come from /pea/status so the backend stays the single source):
// - before five years, any withdrawal closes the plan (outside the cases set
//   by law) and the whole plan's net gain bears income tax + social
//   contributions;
// - after five years, partial withdrawals keep the plan open; only social
//   contributions are due, on the gain share of the amount withdrawn
//   (gains / value prorata). Withdrawals never free deposit room.

const round2 = (v) => Math.round(v * 100) / 100;

const scenario = ({ value, deposits, matured, amount, socialRate, incomeTaxRate }) => {
  const gains = value - deposits;
  if (!matured) {
    const taxableGain = Math.max(0, gains);
    const incomeTax = taxableGain * incomeTaxRate;
    const social = taxableGain * socialRate;
    return {
      closes: true,
      withdrawn: round2(value),
      taxableGain: round2(taxableGain),
      incomeTax: round2(incomeTax),
      social: round2(social),
      taxes: round2(incomeTax + social),
      net: round2(value - incomeTax - social),
      remainingValue: 0,
    };
  }
  const withdrawn = Math.min(amount, value);
  const taxableGain = gains > 0 && value > 0 ? (withdrawn * gains) / value : 0;
  const social = taxableGain * socialRate;
  return {
    closes: false,
    withdrawn: round2(withdrawn),
    taxableGain: round2(taxableGain),
    incomeTax: 0,
    social: round2(social),
    taxes: round2(social),
    net: round2(withdrawn - social),
    remainingValue: round2(value - withdrawn),
  };
};

// Returns null until the status has a value, deposits and a positive amount.
// When the plan is not yet five years old, `afterMaturity` tells what the
// same withdrawal would cost once it is.
export const simulateWithdrawal = (status, amount) => {
  const value = status?.current_value;
  const deposits = status?.deposits;
  if (value === null || value === undefined || deposits === null || deposits === undefined || !(amount > 0)) return null;
  const rates = { socialRate: (status.social_rate_pct ?? 18.6) / 100, incomeTaxRate: (status.income_tax_rate_pct ?? 12.8) / 100 };
  const now = scenario({ value, deposits, matured: Boolean(status.matured), amount, ...rates });
  return {
    ...now,
    requested: round2(amount),
    afterMaturity: status.matured ? null : scenario({ value, deposits, matured: true, amount, ...rates }),
  };
};

export const ceilingUsedPct = (status) => (status?.deposits === null || status?.deposits === undefined
  ? null
  : Math.min(100, Math.max(0, (status.deposits / (status.ceiling || 150000)) * 100)));
